// Complete factor data with 200 questions across 20 factors
// Each factor has 10 questions (5 points max each = 50 points per factor)

export const FACTOR_DATA = {
  // ==========================================
  // FACTOR 1: Emotional Stability & Conflict Resolution
  // Questions 1-10
  // ==========================================
  1: {
    name: "Emotional Stability & Conflict Resolution",
    questions: [
      {
        id: 1,
        text: "How do you typically react when you're upset with your partner?",
        insight: "Reveals maturity in emotional processing and self-control during conflict.",
        answerFormat: "custom",
        options: [
          { value: 1, text: "I shut down, withdraw, or give the silent treatment" },
          { value: 2, text: "I raise my voice or express frustration intensely" },
          { value: 3, text: "I try to stay calm but sometimes struggle" },
          { value: 4, text: "I express my feelings respectfully and seek resolution" },
          { value: 5, text: "I pause, pray, and communicate calmly when ready" }
        ]
      },
      {
        id: 2,
        text: "When we disagree, we can discuss the issue calmly without raised voices or anger.",
        insight: "Tests if couples model Christ-like humility and respect during tension."
      },
      {
        id: 3,
        text: "I find it easy to apologize and take responsibility when I'm wrong.",
        insight: "Measures humility — a key Christlike virtue in marriage."
      },
      {
        id: 4,
        text: "During arguments, I focus more on solving the problem than on proving my point.",
        insight: "Reveals ability to prioritize peace and unity over ego."
      },
      {
        id: 5,
        text: "If my partner hurts me emotionally, I forgive easily and move on.",
        insight: "Tests emotional resilience and Christlike forgiveness."
      },
      {
        id: 6,
        text: "When emotions run high, I prefer to take a break before continuing the discussion.",
        insight: "Reveals ability to self-regulate and avoid destructive escalation."
      },
      {
        id: 7,
        text: "I often replay conflicts in my mind and struggle to let them go.",
        insight: "Tests for lingering bitterness and unresolved emotional wounds."
      },
      {
        id: 8,
        text: "I can identify my emotions and communicate them clearly to my partner.",
        insight: "Assesses self-awareness and openness, foundational to emotional intimacy."
      },
      {
        id: 9,
        text: "When offended, I pray or reflect before responding.",
        insight: "Reveals spiritual discipline in handling anger biblically."
      },
      {
        id: 10,
        text: "We can move past disagreements quickly and restore closeness.",
        insight: "Evaluates reconciliation ability and emotional recovery rate."
      }
    ],
    scoringGuidance: {
      excellent: {
        range: [41, 50],
        level: "Excellent",
        message: "Emotionally mature, forgiving, quick to reconcile.",
        pastoralGuidance: "Encourage continued prayer partnership, accountability, and serving others together."
      },
      developing: {
        range: [31, 40],
        level: "Developing",
        message: "Good awareness but needs growth in self-regulation.",
        pastoralGuidance: "Teach biblical conflict principles (James 1:19–20, Eph. 4:26). Encourage journaling and couple devotionals."
      },
      vulnerable: {
        range: [20, 30],
        level: "Vulnerable",
        message: "Tends toward defensiveness or emotional shutdown.",
        pastoralGuidance: "Recommend 1-on-1 counseling, forgiveness exercises, and conflict coaching."
      }
    }
  },

  // ==========================================
  // FACTOR 2: Communication Style & Intimacy
  // Questions 11-20
  // ==========================================
  2: {
    name: "Communication Style & Intimacy",
    questions: [
      {
        id: 11,
        text: "When we disagree, I feel safe expressing my honest thoughts without fear of being misunderstood or judged.",
        insight: "Assesses emotional safety — essential for openness in marriage."
      },
      {
        id: 12,
        text: "How do you usually respond when your partner shares something deeply emotional?",
        insight: "Reveals empathy and listening capacity — foundations of intimacy.",
        answerFormat: "custom",
        options: [
          { value: 1, text: "I feel uncomfortable and change the subject" },
          { value: 2, text: "I try to fix the problem immediately with advice" },
          { value: 3, text: "I listen but sometimes get distracted" },
          { value: 4, text: "I listen attentively and offer comfort" },
          { value: 5, text: "I give full attention, validate their feelings, and pray with them" }
        ]
      },
      {
        id: 13,
        text: "We often affirm each other with encouraging words and compliments.",
        insight: "Measures verbal affection — a key intimacy builder (Proverbs 16:24)."
      },
      {
        id: 14,
        text: "How do you handle it when your partner interrupts or misunderstands you?",
        insight: "Evaluates patience and clarity in communication under tension.",
        answerFormat: "custom",
        options: [
          { value: 1, text: "I get frustrated and shut down or raise my voice" },
          { value: 2, text: "I feel hurt and become defensive" },
          { value: 3, text: "I try to clarify but sometimes lose patience" },
          { value: 4, text: "I calmly ask them to let me finish and re-explain" },
          { value: 5, text: "I stay patient, gently clarify, and ensure mutual understanding" }
        ]
      },
      {
        id: 15,
        text: "We regularly share our daily experiences, dreams, and spiritual reflections.",
        insight: "Measures emotional closeness and spiritual intimacy rhythm."
      },
      {
        id: 16,
        text: "When tension arises, we can pause and pray together before continuing the discussion.",
        insight: "Tests ability to bring Christ into communication moments."
      },
      {
        id: 17,
        text: "How often do you express affection through touch (hugs, holding hands, sitting close)?",
        insight: "Assesses comfort with physical intimacy as part of connection.",
        answerFormat: "frequency"
      },
      {
        id: 18,
        text: "When my partner speaks, I give my full attention without distractions (phone, TV, multitasking).",
        insight: "Tests active listening — a sign of love and respect.",
        answerFormat: "frequency"
      },
      {
        id: 19,
        text: "I can express my needs directly without fear of being labeled as demanding or emotional.",
        insight: "Assesses freedom to be authentic without fear of rejection."
      },
      {
        id: 20,
        text: "When communication breaks down, I take initiative to restore connection.",
        insight: "Evaluates reconciliation motivation and emotional leadership."
      }
    ],
    scoringGuidance: {
      excellent: {
        range: [41, 50],
        level: "Excellent",
        message: "Deep mutual understanding, emotional transparency, and active listening are well-developed.",
        pastoralGuidance: "Encourage maintaining daily communication rituals and prayerful reflection together."
      },
      developing: {
        range: [31, 40],
        level: "Growing",
        message: "Solid foundation but needs intentional practice in emotional sharing or conflict conversations.",
        pastoralGuidance: "Suggest weekly check-ins and devotional discussions."
      },
      vulnerable: {
        range: [20, 30],
        level: "Vulnerable",
        message: "Risk of miscommunication, withdrawal, or emotional distance.",
        pastoralGuidance: "Encourage communication workshops or couple journaling guided by Ephesians 4:29."
      }
    }
  },

  // ==========================================
  // FACTOR 3: Financial Management & Stewardship
  // Questions 21-30
  // ==========================================
  3: {
    name: "Financial Management & Stewardship",
    questions: [
      {
        id: 21,
        text: "We have discussed our financial goals and how we plan to manage money together.",
        insight: "Measures communication and planning around finances."
      },
      {
        id: 22,
        text: "How would you describe your approach to spending and saving?",
        insight: "Assesses financial discipline and stewardship mindset.",
        answerFormat: "custom",
        options: [
          { value: 1, text: "I spend freely and rarely think about saving" },
          { value: 2, text: "I try to save but often overspend" },
          { value: 3, text: "I balance spending and saving but not consistently" },
          { value: 4, text: "I prioritize saving and spend carefully" },
          { value: 5, text: "I budget intentionally, save consistently, and give generously" }
        ]
      },
      {
        id: 23,
        text: "We are transparent about our debts, income, and financial obligations.",
        insight: "Tests honesty and transparency in financial matters."
      },
      {
        id: 24,
        text: "We have decided how to handle shared expenses and individual spending freedom.",
        insight: "Assesses balance between unity and independence in money use."
      },
      {
        id: 25,
        text: "How do you view giving (tithes, offerings, or charity) in your financial life?",
        insight: "Reveals stewardship maturity and faith-based generosity.",
        answerFormat: "custom",
        options: [
          { value: 1, text: "I rarely think about giving or don't prioritize it" },
          { value: 2, text: "I give occasionally when I have extra" },
          { value: 3, text: "I give regularly but not a fixed amount" },
          { value: 4, text: "I tithe consistently and give offerings when possible" },
          { value: 5, text: "Giving is my first priority — I tithe faithfully and give generously beyond" }
        ]
      },
      {
        id: 26,
        text: "We have discussed how we will make large financial decisions together (e.g., house, car, business).",
        insight: "Assesses unity in decision-making and long-term trust."
      },
      {
        id: 27,
        text: "We agree on how to prioritize financial responsibilities (e.g., bills, savings, giving, leisure).",
        insight: "Evaluates shared value system in resource allocation."
      },
      {
        id: 28,
        text: "We understand each other's financial background and upbringing (e.g., attitudes toward money, saving, risk).",
        insight: "Reveals self-awareness and empathy for financial habits shaped by family history."
      },
      {
        id: 29,
        text: "We have an agreed plan for managing debt and long-term savings or investments.",
        insight: "Assesses planning for stability and wealth stewardship."
      },
      {
        id: 30,
        text: "We believe God is the true source of our provision, and we are stewards of His resources.",
        insight: "Tests spiritual attitude toward wealth and dependence on God."
      }
    ],
    scoringGuidance: {
      excellent: {
        range: [41, 50],
        level: "Excellent",
        message: "Transparent, prayerful, and disciplined financial team.",
        pastoralGuidance: "Encourage joint budgeting and financial mentoring for others."
      },
      developing: {
        range: [31, 40],
        level: "Developing",
        message: "General alignment but lacks detailed planning or spiritual integration.",
        pastoralGuidance: "Suggest joint budgeting sessions and a faith-based finance course."
      },
      vulnerable: {
        range: [20, 30],
        level: "Vulnerable",
        message: "Financial secrecy, debt stress, or clashing habits.",
        pastoralGuidance: "Recommend financial counseling, stewardship workshops, and joint accountability."
      }
    }
  },

  // ==========================================
  // FACTOR 4: Social Compatibility & Family Dynamics
  // Questions 31-40
  // ==========================================
  4: {
    name: "Social Compatibility & Family Dynamics",
    questions: [
      {
        id: 31,
        text: "We both enjoy similar social activities and ways of spending our leisure time.",
        insight: "Assesses compatibility in lifestyle and social engagement preferences."
      },
      {
        id: 32,
        text: "How do you prefer spending weekends or social gatherings?",
        insight: "Reveals the couple's desire for togetherness and balance of independence.",
        answerFormat: "custom",
        options: [
          { value: 1, text: "I prefer being alone or with my own friends separately" },
          { value: 2, text: "I enjoy occasional time together but mostly need my space" },
          { value: 3, text: "I like a mix of together time and individual activities" },
          { value: 4, text: "I prefer spending most leisure time together" },
          { value: 5, text: "I love being together and enjoy shared activities with friends and family" }
        ]
      },
      {
        id: 33,
        text: "We feel comfortable introducing each other to our friends and family.",
        insight: "Tests social pride, identity, and openness to community integration."
      },
      {
        id: 34,
        text: "How do you typically manage time between your family and your partner's family?",
        insight: "Evaluates balance and fairness in extended family relations.",
        answerFormat: "custom",
        options: [
          { value: 1, text: "I prioritize my family and rarely consider my partner's family" },
          { value: 2, text: "I lean toward my family but try to include my partner's sometimes" },
          { value: 3, text: "We haven't discussed it much and just go with the flow" },
          { value: 4, text: "We try to balance time fairly between both families" },
          { value: 5, text: "We intentionally plan and communicate to honor both families equally" }
        ]
      },
      {
        id: 35,
        text: "We have discussed and agreed on how much influence our parents should have in our marriage decisions.",
        insight: "Tests boundary awareness with family and future independence."
      },
      {
        id: 36,
        text: "When family members disagree with our choices, how do you handle it?",
        insight: "Assesses loyalty and emotional maturity in family tension.",
        answerFormat: "custom",
        options: [
          { value: 1, text: "I usually side with my family even if it upsets my partner" },
          { value: 2, text: "I feel torn and avoid the conflict altogether" },
          { value: 3, text: "I try to keep both sides happy but it's stressful" },
          { value: 4, text: "I stand with my partner while respectfully hearing family concerns" },
          { value: 5, text: "We present a united front and address family concerns together with grace" }
        ]
      },
      {
        id: 37,
        text: "We share similar levels of social engagement (e.g., attending church, community events, outings).",
        insight: "Evaluates shared rhythm in community participation and ministry."
      },
      {
        id: 38,
        text: "How comfortable are you in discussing sensitive family matters with your partner?",
        insight: "Tests trust and vulnerability regarding family issues.",
        answerFormat: "comfort"
      },
      {
        id: 39,
        text: "We respect and support each other's friendships outside our relationship.",
        insight: "Evaluates security, boundaries, and emotional trust."
      },
      {
        id: 40,
        text: "When it comes to holiday traditions or family celebrations, we prefer:",
        insight: "Explores creativity, unity, and boundary formation post-marriage.",
        answerFormat: "custom",
        options: [
          { value: 1, text: "Following only one family's traditions" },
          { value: 2, text: "Alternating between families each year" },
          { value: 3, text: "Splitting time between both families during holidays" },
          { value: 4, text: "Creating our own traditions while honoring both families" },
          { value: 5, text: "Blending the best of both families and creating new meaningful traditions together" }
        ]
      }
    ],
    scoringGuidance: {
      excellent: {
        range: [41, 50],
        level: "Excellent",
        message: "Highly adaptable couple with strong family boundaries, shared social life, and emotional unity.",
        pastoralGuidance: "Encourage maintaining family respect and unity through joint traditions and shared hospitality."
      },
      developing: {
        range: [31, 40],
        level: "Developing",
        message: "Fair compatibility, but may need alignment in social energy or family involvement.",
        pastoralGuidance: "Suggest setting family boundary agreements and shared social calendars."
      },
      vulnerable: {
        range: [20, 30],
        level: "Vulnerable",
        message: "Family pressure or social mismatch may cause conflict.",
        pastoralGuidance: "Counsel on Genesis 2:24 ('leave and cleave'), and teach communication strategies with both families."
      }
    }
  },

  // ==========================================
  // FACTOR 5: Life Planning & Flexibility
  // Questions 41-50
  // ==========================================
  5: {
    name: "Life Planning & Flexibility",
    questions: [
      {
        id: 41,
        text: "We have talked about our long-term goals such as where to live, career plans, and family vision.",
        insight: "Assesses shared direction and clarity of purpose in the relationship."
      },
      {
        id: 42,
        text: "If unexpected life changes occur (job loss, relocation, illness), how would you typically respond?",
        insight: "Reveals adaptability and resilience under pressure.",
        answerFormat: "custom",
        options: [
          { value: 1, text: "I panic, blame others, or shut down emotionally" },
          { value: 2, text: "I struggle to cope and need time to process alone" },
          { value: 3, text: "I feel stressed but eventually adapt" },
          { value: 4, text: "I stay calm and work with my partner to find solutions" },
          { value: 5, text: "I trust God, pray together, and adapt with faith and teamwork" }
        ]
      },
      {
        id: 43,
        text: "We pray and plan together before making significant life decisions.",
        insight: "Tests spiritual partnership and unity in direction-setting."
      },
      {
        id: 44,
        text: "How do you view the balance between work, ministry, and family life?",
        insight: "Reveals life-priority hierarchy and values system.",
        answerFormat: "custom",
        options: [
          { value: 1, text: "Work comes first — family and ministry fit around it" },
          { value: 2, text: "I struggle to find balance and often feel overwhelmed" },
          { value: 3, text: "I try to balance but one area often dominates" },
          { value: 4, text: "Family and ministry are priorities, with work supporting them" },
          { value: 5, text: "God first, then family, then work — we align our schedule accordingly" }
        ]
      },
      {
        id: 45,
        text: "We are open to adjusting our plans if God leads us differently.",
        insight: "Evaluates spiritual flexibility and obedience to divine direction."
      },
      {
        id: 46,
        text: "How do you typically handle disagreements about life goals or timing (e.g., when to buy a house, have children)?",
        insight: "Tests willingness to collaborate and adjust expectations.",
        answerFormat: "custom",
        options: [
          { value: 1, text: "I insist on my timeline and feel frustrated if we don't agree" },
          { value: 2, text: "I give in to avoid conflict but feel resentful" },
          { value: 3, text: "We discuss but often reach a stalemate" },
          { value: 4, text: "We compromise and find middle ground together" },
          { value: 5, text: "We pray, discuss openly, and trust God's timing together" }
        ]
      },
      {
        id: 47,
        text: "We have talked about retirement plans or how we want to spend our later years together.",
        insight: "Shows long-term vision and shared purpose beyond immediate seasons."
      },
      {
        id: 48,
        text: "I am comfortable with changing plans or roles when necessary (e.g., job changes, moving, childcare adjustments).",
        insight: "Assesses adaptability and partnership in transitions."
      },
      {
        id: 49,
        text: "How would you handle a scenario where one partner's career requires relocation?",
        insight: "Measures flexibility, mutual sacrifice, and teamwork.",
        answerFormat: "custom",
        options: [
          { value: 1, text: "I would refuse to move for my partner's career" },
          { value: 2, text: "I would struggle significantly and feel resentful" },
          { value: 3, text: "I would consider it but need significant convincing" },
          { value: 4, text: "I would support the move if it's best for us both" },
          { value: 5, text: "We would pray together and make the decision as a united team" }
        ]
      },
      {
        id: 50,
        text: "We are united in our understanding of God's purpose for our marriage.",
        insight: "Assesses spiritual alignment in life purpose and direction."
      }
    ],
    scoringGuidance: {
      excellent: {
        range: [41, 50],
        level: "Excellent",
        message: "Couple has a clear, faith-led life vision and strong adaptability to change.",
        pastoralGuidance: "Encourage vision retreats (annual or biannual) to revisit shared goals and pray over life direction."
      },
      developing: {
        range: [31, 40],
        level: "Developing",
        message: "Good teamwork but may need clarity in purpose or career balance.",
        pastoralGuidance: "Recommend couple goal-setting sessions and mentorship from older couples."
      },
      vulnerable: {
        range: [20, 30],
        level: "Vulnerable",
        message: "Unclear or conflicting goals may cause confusion or tension.",
        pastoralGuidance: "Encourage spiritual counseling on calling, purpose, and flexibility through Proverbs 16:9 reflection."
      }
    }
  },

  // ==========================================
  // FACTOR 6: Love Languages
  // Questions 51-60
  // ==========================================
  6: {
    name: "Love Languages",
    questions: [
      {
        id: 51,
        text: "I feel most loved when my partner speaks kind and encouraging words to me.",
        insight: "Tests sensitivity to verbal affirmation as a primary love language."
      },
      {
        id: 52,
        text: "How do you usually express love to your partner?",
        insight: "Identifies the partner's dominant love expression style.",
        answerFormat: "custom",
        options: [
          { value: 1, text: "I rarely express love openly or intentionally" },
          { value: 2, text: "I show love occasionally but not consistently" },
          { value: 3, text: "I express love in my own way but may not match their needs" },
          { value: 4, text: "I try to express love in ways meaningful to them" },
          { value: 5, text: "I intentionally speak their love language — words, time, gifts, service, or touch" }
        ]
      },
      {
        id: 53,
        text: "I feel most loved when my partner spends quality, uninterrupted time with me.",
        insight: "Measures the importance of quality time for bonding and security."
      },
      {
        id: 54,
        text: "When my partner helps me with practical tasks (like chores or errands), I feel loved.",
        insight: "Assesses 'Acts of Service' as a love language — love demonstrated through help."
      },
      {
        id: 55,
        text: "I feel most cared for when my partner surprises me with gifts or tokens of appreciation.",
        insight: "Explores material expression of affection (Receiving Gifts)."
      },
      {
        id: 56,
        text: "How do you react when your partner shows affection through physical touch?",
        insight: "Evaluates comfort level with the Physical Touch love language.",
        answerFormat: "custom",
        options: [
          { value: 1, text: "I feel uncomfortable and prefer minimal physical contact" },
          { value: 2, text: "I tolerate it but don't seek it out" },
          { value: 3, text: "I appreciate it sometimes depending on my mood" },
          { value: 4, text: "I enjoy it and feel loved when touched affectionately" },
          { value: 5, text: "I feel deeply connected and cherished through physical affection" }
        ]
      },
      {
        id: 57,
        text: "My partner and I have discussed and identified each other's primary love languages.",
        insight: "Tests communication of love awareness and intentionality."
      },
      {
        id: 58,
        text: "I notice when my partner expresses love in a way that is different from mine, and I still appreciate it.",
        insight: "Measures maturity and flexibility in interpreting love gestures."
      },
      {
        id: 59,
        text: "I make a conscious effort to express love in ways that are meaningful to my partner.",
        insight: "Assesses selflessness and adaptability in expressing affection."
      },
      {
        id: 60,
        text: "Our love feels stronger when we intentionally speak each other's love languages regularly.",
        insight: "Evaluates whether love expression is habitual or circumstantial."
      }
    ],
    scoringGuidance: {
      excellent: {
        range: [41, 50],
        level: "Excellent",
        message: "Deep emotional intelligence; couple knows how to give and receive love intentionally.",
        pastoralGuidance: "Encourage maintaining a rhythm of affection through weekly 'love check-ins.' Reinforce Ephesians 5:2 – 'Walk in love, as Christ loved us.'"
      },
      developing: {
        range: [31, 40],
        level: "Developing",
        message: "Understanding present but inconsistent in practice; still learning one another's emotional cues.",
        pastoralGuidance: "Suggest Love Language workshops or couple reading sessions (The 5 Love Languages by Gary Chapman)."
      },
      vulnerable: {
        range: [20, 30],
        level: "Vulnerable",
        message: "Emotional disconnection or mismatched expectations of love.",
        pastoralGuidance: "Recommend guided exercises to discover and practice each other's love languages; integrate affection into daily prayer life."
      }
    }
  },

  // ==========================================
  // FACTOR 7: Personality & Temperament
  // Questions 61-70
  // ==========================================
  7: {
    name: "Personality & Temperament",
    questions: [
      {
        id: 61,
        text: "I gain energy from being around people rather than being alone.",
        insight: "Assesses introversion–extroversion tendencies and social compatibility."
      },
      {
        id: 62,
        text: "When making decisions, I rely more on logic and reasoning than on emotions.",
        insight: "Evaluates Thinking vs. Feeling orientation in decision-making."
      },
      {
        id: 63,
        text: "How would you describe your approach to planning?",
        insight: "Tests Judging vs. Perceiving (structured vs. spontaneous) lifestyle preferences.",
        answerFormat: "custom",
        options: [
          { value: 1, text: "I avoid planning and prefer complete spontaneity" },
          { value: 2, text: "I make loose plans but rarely follow through" },
          { value: 3, text: "I plan for important things but stay flexible" },
          { value: 4, text: "I like having structured plans and schedules" },
          { value: 5, text: "I plan thoroughly and enjoy organizing details in advance" }
        ]
      },
      {
        id: 64,
        text: "I tend to focus more on details than on the big picture.",
        insight: "Measures Sensing vs. Intuition — practicality versus imagination."
      },
      {
        id: 65,
        text: "When under stress, how do you usually respond?",
        insight: "Assesses emotional stability and coping under pressure.",
        answerFormat: "custom",
        options: [
          { value: 1, text: "I become irritable, anxious, or shut down completely" },
          { value: 2, text: "I struggle to cope and need to be alone" },
          { value: 3, text: "I manage but feel overwhelmed inside" },
          { value: 4, text: "I stay calm and work through challenges methodically" },
          { value: 5, text: "I turn to prayer, stay composed, and seek support from my partner" }
        ]
      },
      {
        id: 66,
        text: "I enjoy leading and making decisions for the group when needed.",
        insight: "Measures leadership drive (Choleric tendencies)."
      },
      {
        id: 67,
        text: "I often prioritize peace over being right during disagreements.",
        insight: "Tests Phlegmatic temperament — valuing harmony and calmness."
      },
      {
        id: 68,
        text: "I am generally cheerful, talkative, and sociable in most situations.",
        insight: "Reveals Sanguine temperament — enthusiasm and expressiveness."
      },
      {
        id: 69,
        text: "When working on a project, I prefer accuracy and order over speed and experimentation.",
        insight: "Measures Melancholic temperament — perfectionist and detailed-oriented."
      },
      {
        id: 70,
        text: "I understand and appreciate that my partner's personality complements mine, even when we differ.",
        insight: "Evaluates appreciation of diversity and emotional intelligence in temperament differences."
      }
    ],
    scoringGuidance: {
      excellent: {
        range: [41, 50],
        level: "Excellent",
        message: "Emotionally balanced couple with strong self-awareness and mutual respect for differences.",
        pastoralGuidance: "Encourage continued appreciation of God's unique design in each temperament (Romans 12:6). Suggest a 'temperament appreciation day' where each partner celebrates the other's traits."
      },
      developing: {
        range: [31, 40],
        level: "Developing",
        message: "Some personality understanding present but occasional tension from differing traits.",
        pastoralGuidance: "Encourage deeper empathy and personality awareness workshops (e.g., MBTI or DISC exercises)."
      },
      vulnerable: {
        range: [20, 30],
        level: "Vulnerable",
        message: "Personality mismatch or misunderstanding may cause miscommunication or frustration.",
        pastoralGuidance: "Recommend guided counseling focused on understanding emotional patterns and embracing differences as strengths."
      }
    }
  },

  // ==========================================
  // FACTOR 8: Spiritual Compatibility & Values
  // Questions 71-80
  // ==========================================
  8: {
    name: "Spiritual Compatibility & Values",
    questions: [
      {
        id: 71,
        text: "We share the same core Christian beliefs and values about faith and salvation.",
        insight: "Assesses theological unity and shared spiritual foundation."
      },
      {
        id: 72,
        text: "How often do you and your partner pray together?",
        insight: "Measures joint spiritual discipline and shared prayer life.",
        answerFormat: "frequency"
      },
      {
        id: 73,
        text: "We agree on how faith influences our daily choices and moral decisions.",
        insight: "Evaluates application of shared faith in practical living."
      },
      {
        id: 74,
        text: "When one of us faces a spiritual struggle, we can talk about it openly without judgment.",
        insight: "Assesses grace, vulnerability, and mutual accountability."
      },
      {
        id: 75,
        text: "We attend church and serve in ministry together or in complementary ways.",
        insight: "Measures involvement in shared worship and service."
      },
      {
        id: 76,
        text: "How do you usually handle disagreements about spiritual matters (e.g., doctrines, church preferences)?",
        insight: "Reveals maturity and humility in spiritual discussions.",
        answerFormat: "custom",
        options: [
          { value: 1, text: "I insist I'm right and avoid further discussion" },
          { value: 2, text: "I feel defensive and the topic causes tension" },
          { value: 3, text: "We agree to disagree without resolution" },
          { value: 4, text: "We discuss respectfully and seek understanding" },
          { value: 5, text: "We pray together, study Scripture, and humbly seek God's truth" }
        ]
      },
      {
        id: 77,
        text: "We have discussed how we will raise our children in the faith (e.g., prayer, church, teaching).",
        insight: "Tests unity in spiritual parenting philosophy."
      },
      {
        id: 78,
        text: "How would you describe your personal spiritual growth compared to your partner's?",
        insight: "Measures mutual discipleship and spiritual pace.",
        answerFormat: "custom",
        options: [
          { value: 1, text: "We are at very different spiritual levels with no plan to grow together" },
          { value: 2, text: "One of us is significantly more mature spiritually" },
          { value: 3, text: "We are growing but at different paces" },
          { value: 4, text: "We are growing together at a similar pace" },
          { value: 5, text: "We actively disciple and encourage each other's spiritual growth daily" }
        ]
      },
      {
        id: 79,
        text: "We both believe that serving others and giving are vital expressions of our faith.",
        insight: "Evaluates active faith through service and generosity."
      },
      {
        id: 80,
        text: "We see our marriage as part of God's purpose for advancing His kingdom.",
        insight: "Measures theological understanding of marriage as a divine calling."
      }
    ],
    scoringGuidance: {
      excellent: {
        range: [41, 50],
        level: "Excellent",
        message: "Deeply united spiritually, with shared prayer, service, and purpose.",
        pastoralGuidance: "Encourage joint devotionals, ministry partnership, and continual spiritual check-ins."
      },
      developing: {
        range: [31, 40],
        level: "Developing",
        message: "Shared faith but inconsistent spiritual rhythm or differences in intensity.",
        pastoralGuidance: "Suggest building regular prayer routines and shared Bible reading."
      },
      vulnerable: {
        range: [20, 30],
        level: "Vulnerable",
        message: "Unequal or uncoordinated spiritual growth patterns.",
        pastoralGuidance: "Recommend mentorship under a spiritually mature couple and faith alignment counseling."
      }
    }
  },

  // ==========================================
  // FACTOR 9: Parenting Philosophy & Family Vision
  // Questions 81-90
  // ==========================================
  9: {
    name: "Parenting Philosophy & Family Vision",
    questions: [
      {
        id: 81,
        text: "We have discussed how many children we would like to have and why.",
        insight: "Assesses clarity and unity about family planning and expectations."
      },
      {
        id: 82,
        text: "How do you both view the role of discipline in raising children?",
        insight: "Evaluates parental balance between correction and compassion.",
        answerFormat: "custom",
        options: [
          { value: 1, text: "We have very different views and haven't discussed it" },
          { value: 2, text: "We disagree on discipline methods and it causes tension" },
          { value: 3, text: "We somewhat agree but haven't defined our approach" },
          { value: 4, text: "We agree on balanced discipline with love and correction" },
          { value: 5, text: "We are united on biblical discipline — firm, loving, and consistent" }
        ]
      },
      {
        id: 83,
        text: "We agree on whether one parent should stay home or how childcare will be managed.",
        insight: "Assesses mutual understanding of caregiving roles."
      },
      {
        id: 84,
        text: "We have similar views on whether to use public, private, or homeschool education.",
        insight: "Tests compatibility in education priorities and faith integration."
      },
      {
        id: 85,
        text: "I believe both parents should model faith through daily example, not just church attendance.",
        insight: "Measures spiritual leadership vision in parenting."
      },
      {
        id: 86,
        text: "How would you handle differences in parenting opinions between you and your partner?",
        insight: "Assesses teamwork and emotional intelligence in co-parenting.",
        answerFormat: "custom",
        options: [
          { value: 1, text: "I would insist on my way since I know best" },
          { value: 2, text: "I would argue my point until we agree" },
          { value: 3, text: "We would compromise but might still feel frustrated" },
          { value: 4, text: "We would discuss calmly and find common ground" },
          { value: 5, text: "We would pray together, discuss openly, and present a united front to our children" }
        ]
      },
      {
        id: 87,
        text: "We have discussed how to raise our children in Christian faith and moral values.",
        insight: "Assesses shared spiritual parenting plan."
      },
      {
        id: 88,
        text: "We are aware of how our own upbringing may influence our future parenting style.",
        insight: "Encourages self-awareness and reflection on generational patterns."
      },
      {
        id: 89,
        text: "We are both willing to seek parenting advice or counseling if challenges arise.",
        insight: "Measures humility and teachability as future parents."
      },
      {
        id: 90,
        text: "We see parenting as a shared ministry, not just a responsibility.",
        insight: "Assesses spiritual and missional vision in family life."
      }
    ],
    scoringGuidance: {
      excellent: {
        range: [41, 50],
        level: "Excellent",
        message: "Shared parenting vision rooted in love, discipline, and spiritual formation.",
        pastoralGuidance: "Encourage mentoring other young families; reinforce regular prayer for future children."
      },
      developing: {
        range: [31, 40],
        level: "Developing",
        message: "Agree on major values but lack structure or deep discussions.",
        pastoralGuidance: "Suggest detailed planning workshops on family routines and faith development."
      },
      vulnerable: {
        range: [20, 30],
        level: "Vulnerable",
        message: "Conflicting or unclear parenting beliefs.",
        pastoralGuidance: "Recommend family vision-building sessions and generational healing studies."
      }
    }
  },

  // ==========================================
  // FACTOR 10: Sexual Intimacy & Expectations
  // Questions 91-100
  // ==========================================
  10: {
    name: "Sexual Intimacy & Expectations",
    questions: [
      {
        id: 91,
        text: "We have openly discussed our expectations and boundaries regarding physical intimacy before marriage.",
        insight: "Assesses communication openness on sexual expectations and boundaries."
      },
      {
        id: 92,
        text: "I believe sexual intimacy in marriage is both physical and spiritual — an expression of love, not just desire.",
        insight: "Measures understanding of God's design for sex as covenantal and holy."
      },
      {
        id: 93,
        text: "How comfortable do you feel discussing sexual matters with your partner?",
        insight: "Tests emotional safety and transparency in communication about intimacy.",
        answerFormat: "comfort"
      },
      {
        id: 94,
        text: "We have talked about how past experiences (or trauma) might affect our sexual relationship.",
        insight: "Assesses vulnerability and healing awareness."
      },
      {
        id: 95,
        text: "We believe sexual intimacy should be mutual — about giving, not just receiving pleasure.",
        insight: "Evaluates selflessness and partnership in intimacy (1 Cor. 7:3–4)."
      },
      {
        id: 96,
        text: "How do you respond when your partner has a different level of sexual desire or need than you?",
        insight: "Measures emotional maturity and empathy regarding sexual differences.",
        answerFormat: "custom",
        options: [
          { value: 1, text: "I feel rejected or frustrated and withdraw" },
          { value: 2, text: "I struggle to understand and it causes tension" },
          { value: 3, text: "I try to be patient but sometimes feel hurt" },
          { value: 4, text: "I communicate openly and try to understand their perspective" },
          { value: 5, text: "I respond with grace, patience, and loving communication" }
        ]
      },
      {
        id: 97,
        text: "We have discussed how we'll keep sexual intimacy alive after marriage despite busy schedules or stress.",
        insight: "Tests intentionality in maintaining intimacy through life seasons."
      },
      {
        id: 98,
        text: "I believe forgiveness and emotional healing are essential for a healthy sexual life in marriage.",
        insight: "Explores the spiritual-emotional link between love, forgiveness, and intimacy."
      },
      {
        id: 99,
        text: "We agree on how to protect our intimacy from distractions or temptations (e.g., pornography, comparison).",
        insight: "Measures moral commitment and sexual boundaries."
      },
      {
        id: 100,
        text: "We see sexual intimacy as a way to strengthen emotional, physical, and spiritual connection in marriage.",
        insight: "Measures holistic understanding of intimacy's divine purpose."
      }
    ],
    scoringGuidance: {
      excellent: {
        range: [41, 50],
        level: "Excellent",
        message: "Mature, respectful, and spiritually grounded view of intimacy.",
        pastoralGuidance: "Encourage continual emotional connection, prayer before intimacy, and guarding purity before marriage."
      },
      developing: {
        range: [31, 40],
        level: "Developing",
        message: "Positive understanding but limited emotional or spiritual depth.",
        pastoralGuidance: "Suggest deeper conversations on boundaries, expectations, and mutual needs."
      },
      vulnerable: {
        range: [20, 30],
        level: "Vulnerable",
        message: "Gaps in communication or understanding of God's design for sex.",
        pastoralGuidance: "Recommend pastoral guidance or pre-marital purity sessions emphasizing covenant intimacy."
      }
    }
  },

  // ==========================================
  // FACTOR 11: Extended Family & Boundaries
  // Questions 101-110
  // ==========================================
  11: {
    name: "Extended Family & Boundaries",
    questions: [
      {
        id: 101,
        text: "We have discussed how involved our parents and relatives should be in our marriage decisions.",
        insight: "Assesses clarity on boundaries regarding family influence."
      },
      {
        id: 102,
        text: "How do you handle it when a family member gives advice that conflicts with your spouse's view?",
        insight: "Reveals loyalty, maturity, and spiritual understanding of marital order.",
        answerFormat: "custom",
        options: [
          { value: 1, text: "I usually follow my family's advice over my partner's" },
          { value: 2, text: "I feel torn and often avoid making a decision" },
          { value: 3, text: "I consider both but struggle to choose" },
          { value: 4, text: "I respectfully hear family but prioritize my partner's view" },
          { value: 5, text: "We discuss together, pray, and make a united decision" }
        ]
      },
      {
        id: 103,
        text: "We have talked about how to celebrate holidays and special occasions with both sides of the family.",
        insight: "Assesses flexibility and fairness in balancing family traditions."
      },
      {
        id: 104,
        text: "I feel comfortable saying 'no' to family requests when necessary to protect our marriage time or finances.",
        insight: "Measures assertiveness and courage in maintaining marital boundaries."
      },
      {
        id: 105,
        text: "Our families respect our privacy and do not interfere in our disagreements or decisions.",
        insight: "Tests family respect for boundaries and couple's united front."
      },
      {
        id: 106,
        text: "How do you and your partner typically respond to extended family conflict?",
        insight: "Evaluates unity and maturity in handling family tensions.",
        answerFormat: "custom",
        options: [
          { value: 1, text: "We take sides against each other" },
          { value: 2, text: "We avoid the issue and let tension build" },
          { value: 3, text: "We try to handle it but often disagree on approach" },
          { value: 4, text: "We discuss privately and approach family united" },
          { value: 5, text: "We pray together, stay united, and address conflict with grace" }
        ]
      },
      {
        id: 107,
        text: "We both understand that leaving and cleaving means creating our own home culture and decisions.",
        insight: "Tests understanding of Genesis 2:24 — emotional and practical independence."
      },
      {
        id: 108,
        text: "We have agreed on financial boundaries when it comes to supporting family members (e.g., parents, siblings).",
        insight: "Assesses clarity on financial responsibility toward extended family."
      },
      {
        id: 109,
        text: "Our families get along well and show mutual respect for one another.",
        insight: "Evaluates harmony between in-laws and mutual respect between families."
      },
      {
        id: 110,
        text: "I am confident that my partner and I can protect our marriage from negative family influences with grace and wisdom.",
        insight: "Tests couple's unity and faith in navigating family pressure."
      }
    ],
    scoringGuidance: {
      excellent: {
        range: [41, 50],
        level: "Excellent",
        message: "Healthy boundaries; family involvement is respectful and balanced.",
        pastoralGuidance: "Encourage couple to honor parents while preserving unity — 'leave and cleave' (Gen. 2:24)."
      },
      developing: {
        range: [31, 40],
        level: "Developing",
        message: "Some healthy boundaries but inconsistent enforcement.",
        pastoralGuidance: "Suggest pre-marital discussions on finances, holiday plans, and conflict roles."
      },
      vulnerable: {
        range: [20, 30],
        level: "Vulnerable",
        message: "Over-influence from relatives or lack of clear boundaries.",
        pastoralGuidance: "Recommend family-systems counseling or guided mentorship to strengthen independence."
      }
    }
  },

  // ==========================================
  // FACTOR 12: Career & Life Goals
  // Questions 111-120
  // ==========================================
  12: {
    name: "Career & Life Goals",
    questions: [
      {
        id: 111,
        text: "We have discussed how our individual career goals align with our shared vision as a couple.",
        insight: "Assesses vision alignment between career ambition and marital purpose."
      },
      {
        id: 112,
        text: "How would you handle a situation where one partner's career requires significant relocation?",
        insight: "Tests adaptability and teamwork in major life transitions.",
        answerFormat: "custom",
        options: [
          { value: 1, text: "I would refuse — my career and roots are more important" },
          { value: 2, text: "I would feel resentful but might eventually agree" },
          { value: 3, text: "I would consider it carefully but need convincing" },
          { value: 4, text: "I would support the move if it benefits our family" },
          { value: 5, text: "We would pray together and make the decision as a united team" }
        ]
      },
      {
        id: 113,
        text: "We support each other's personal growth and professional advancement.",
        insight: "Evaluates encouragement, partnership, and mutual respect in careers."
      },
      {
        id: 114,
        text: "How do you view the balance between work, family, and ministry?",
        insight: "Tests biblical priority alignment (Matthew 6:33).",
        answerFormat: "custom",
        options: [
          { value: 1, text: "Work is my top priority — everything else fits around it" },
          { value: 2, text: "I struggle to balance and one area always suffers" },
          { value: 3, text: "I try to balance but often feel overwhelmed" },
          { value: 4, text: "Family and ministry come first, with work supporting them" },
          { value: 5, text: "God first, family second, then work — we live by this order" }
        ]
      },
      {
        id: 115,
        text: "We have talked about our long-term financial and retirement goals together.",
        insight: "Measures foresight and shared planning for future seasons."
      },
      {
        id: 116,
        text: "How do you respond when your career goals conflict with your partner's availability or calling?",
        insight: "Assesses humility, cooperation, and shared calling awareness.",
        answerFormat: "custom",
        options: [
          { value: 1, text: "My career comes first — they need to adjust" },
          { value: 2, text: "I feel frustrated and struggle to compromise" },
          { value: 3, text: "We try to work it out but tension remains" },
          { value: 4, text: "I consider their needs and find ways to accommodate both" },
          { value: 5, text: "We pray together and seek God's direction for our combined calling" }
        ]
      },
      {
        id: 117,
        text: "We have discussed how to handle financial challenges (e.g., job loss, delayed promotion).",
        insight: "Tests emotional resilience and faith during economic uncertainty."
      },
      {
        id: 118,
        text: "We are both open to adjusting our goals if God leads us in a different direction.",
        insight: "Measures surrender to divine guidance over ambition."
      },
      {
        id: 119,
        text: "How do you feel about one partner temporarily sacrificing their career for the family's stability or purpose?",
        insight: "Assesses flexibility and humility in mutual sacrifice.",
        answerFormat: "custom",
        options: [
          { value: 1, text: "I would never sacrifice my career for family" },
          { value: 2, text: "I would struggle with it and feel resentful" },
          { value: 3, text: "I could consider it but would need time to adjust" },
          { value: 4, text: "I would support it if it's best for our family" },
          { value: 5, text: "I see it as an honor to serve our family's purpose when needed" }
        ]
      },
      {
        id: 120,
        text: "We have prayed together about our career choices and how they fit into God's purpose for our lives.",
        insight: "Evaluates spiritual integration in professional direction."
      }
    ],
    scoringGuidance: {
      excellent: {
        range: [41, 50],
        level: "Excellent",
        message: "Strong unity, shared goals, and Spirit-led flexibility in career planning.",
        pastoralGuidance: "Encourage ongoing 'vision retreats' once a year to revisit goals and pray over direction."
      },
      developing: {
        range: [31, 40],
        level: "Developing",
        message: "Good direction but still adjusting balance between ambition, faith, and family.",
        pastoralGuidance: "Suggest joint goal-mapping sessions and financial stewardship discussions."
      },
      vulnerable: {
        range: [20, 30],
        level: "Vulnerable",
        message: "Conflicting priorities or weak planning habits.",
        pastoralGuidance: "Recommend guided sessions on vocation, stewardship, and aligning ambition with divine calling."
      }
    }
  },

  // ==========================================
  // FACTOR 13: Emotional Intelligence
  // Questions 121-130
  // ==========================================
  13: {
    name: "Emotional Intelligence",
    questions: [
      {
        id: 121,
        text: "I can identify and communicate my emotions clearly when I'm upset or disappointed.",
        insight: "Assesses self-awareness and healthy emotional expression."
      },
      {
        id: 122,
        text: "When my partner is upset, I try to understand their emotions before reacting.",
        insight: "Evaluates empathy and active listening."
      },
      {
        id: 123,
        text: "How do you usually respond during heated disagreements?",
        insight: "Tests self-regulation and conflict management.",
        answerFormat: "custom",
        options: [
          { value: 1, text: "I raise my voice, say hurtful things, or storm off" },
          { value: 2, text: "I shut down and give the silent treatment" },
          { value: 3, text: "I try to stay calm but sometimes lose control" },
          { value: 4, text: "I express my feelings firmly but respectfully" },
          { value: 5, text: "I stay calm, listen actively, and seek resolution together" }
        ]
      },
      {
        id: 124,
        text: "I can apologize easily when I realize I've hurt my partner's feelings.",
        insight: "Measures humility and responsibility in relationships."
      },
      {
        id: 125,
        text: "When I feel overwhelmed, I know healthy ways to calm myself instead of lashing out.",
        insight: "Assesses self-regulation and stress response."
      },
      {
        id: 126,
        text: "I understand how my words and actions affect my partner emotionally.",
        insight: "Evaluates social awareness and relational sensitivity."
      },
      {
        id: 127,
        text: "How do you typically handle feelings of jealousy or insecurity?",
        insight: "Tests self-awareness and emotional honesty in vulnerability.",
        answerFormat: "custom",
        options: [
          { value: 1, text: "I accuse my partner or become controlling" },
          { value: 2, text: "I suppress feelings but they affect my behavior" },
          { value: 3, text: "I struggle internally but try not to show it" },
          { value: 4, text: "I acknowledge my feelings and talk to my partner" },
          { value: 5, text: "I bring my insecurities to God and communicate openly with my partner" }
        ]
      },
      {
        id: 128,
        text: "I can forgive quickly and let go of emotional grudges.",
        insight: "Measures capacity for forgiveness and emotional renewal."
      },
      {
        id: 129,
        text: "When my partner shares feedback or correction, I listen without getting defensive.",
        insight: "Assesses teachability and humility in feedback situations."
      },
      {
        id: 130,
        text: "We pray or pause together when emotions run high, rather than letting anger take control.",
        insight: "Combines spiritual maturity with emotional discipline."
      }
    ],
    scoringGuidance: {
      excellent: {
        range: [41, 50],
        level: "Excellent",
        message: "Emotionally mature couple; practices empathy, forgiveness, and self-control.",
        pastoralGuidance: "Encourage them to lead by example — mentoring younger couples on emotional harmony."
      },
      developing: {
        range: [31, 40],
        level: "Developing",
        message: "Good awareness but occasional struggles with regulation or defensiveness.",
        pastoralGuidance: "Suggest reflection exercises, journaling, and couple prayer before difficult talks."
      },
      vulnerable: {
        range: [20, 30],
        level: "Vulnerable",
        message: "Emotional immaturity or avoidance of feelings.",
        pastoralGuidance: "Recommend guided counseling on communication and emotional awareness through Scripture (e.g., Proverbs 15:1, Galatians 5:22–23)."
      }
    }
  },

  // ==========================================
  // FACTOR 14: Trust & Past Relationships
  // Questions 131-140
  // ==========================================
  14: {
    name: "Trust & Past Relationships",
    questions: [
      {
        id: 131,
        text: "I feel completely safe being honest with my partner, even about uncomfortable topics.",
        insight: "Assesses emotional safety and vulnerability in the relationship."
      },
      {
        id: 132,
        text: "How do you handle situations where your partner has made a mistake or disappointed you?",
        insight: "Evaluates forgiveness and grace under relational strain.",
        answerFormat: "custom",
        options: [
          { value: 1, text: "I hold grudges and bring it up repeatedly" },
          { value: 2, text: "I forgive eventually but it takes a long time" },
          { value: 3, text: "I try to move on but sometimes struggle" },
          { value: 4, text: "I forgive and work through it together" },
          { value: 5, text: "I extend grace quickly, pray together, and restore trust" }
        ]
      },
      {
        id: 133,
        text: "We have shared about our past relationships or emotional experiences openly and respectfully.",
        insight: "Assesses transparency about personal history and emotional readiness."
      },
      {
        id: 134,
        text: "If trust is broken, what is your first instinct?",
        insight: "Reveals reaction to betrayal and readiness for reconciliation.",
        answerFormat: "custom",
        options: [
          { value: 1, text: "End the relationship — trust cannot be restored" },
          { value: 2, text: "Withdraw emotionally and build walls" },
          { value: 3, text: "Feel hurt but consider working through it" },
          { value: 4, text: "Seek to understand what happened and work on restoration" },
          { value: 5, text: "Pray for healing, seek counsel, and commit to rebuilding trust" }
        ]
      },
      {
        id: 135,
        text: "We are open about our online activities and interactions with the opposite sex.",
        insight: "Evaluates transparency in digital behavior and boundaries."
      },
      {
        id: 136,
        text: "I believe my partner is trustworthy even when I'm not present.",
        insight: "Assesses deep confidence in partner's integrity."
      },
      {
        id: 137,
        text: "How do you usually respond when your partner sets a boundary (e.g., privacy, space, or friendship)?",
        insight: "Tests maturity and respect for individuality within unity.",
        answerFormat: "custom",
        options: [
          { value: 1, text: "I feel rejected or suspicious of their motives" },
          { value: 2, text: "I struggle to accept it and push back" },
          { value: 3, text: "I accept it but feel uncomfortable" },
          { value: 4, text: "I respect their boundary and honor their needs" },
          { value: 5, text: "I fully support healthy boundaries as a sign of trust and maturity" }
        ]
      },
      {
        id: 138,
        text: "We have discussed how we will handle trust issues if one of us feels insecure or jealous.",
        insight: "Measures proactive approach to emotional honesty and reassurance."
      },
      {
        id: 139,
        text: "I have fully healed from my past relationships and do not compare my partner to others.",
        insight: "Assesses emotional closure and freedom from past attachments."
      },
      {
        id: 140,
        text: "We pray together to strengthen mutual trust and resist suspicion or fear.",
        insight: "Connects trust-building with spiritual intimacy and unity."
      }
    ],
    scoringGuidance: {
      excellent: {
        range: [41, 50],
        level: "Excellent",
        message: "Trust and transparency are well-developed; both partners value integrity and healing.",
        pastoralGuidance: "Encourage maintaining honesty through shared prayer, accountability, and spiritual mentorship."
      },
      developing: {
        range: [31, 40],
        level: "Developing",
        message: "Good trust foundation but occasional insecurities or emotional residues.",
        pastoralGuidance: "Suggest sessions on emotional healing, vulnerability, and forgiveness."
      },
      vulnerable: {
        range: [20, 30],
        level: "Vulnerable",
        message: "Lingering wounds or secrecy issues affect openness and peace.",
        pastoralGuidance: "Recommend personal healing retreats, forgiveness prayers, and professional or pastoral counseling."
      }
    }
  },

  // ==========================================
  // FACTOR 15: Sexual & Physical Boundaries
  // Questions 141-150
  // ==========================================
  15: {
    name: "Sexual & Physical Boundaries",
    questions: [
      {
        id: 141,
        text: "We have openly discussed our physical boundaries and both agree to honor them until marriage.",
        insight: "Assesses communication, mutual respect, and agreement about physical limits."
      },
      {
        id: 142,
        text: "How do you respond when sexual temptation arises between you and your partner?",
        insight: "Tests discipline and accountability under pressure.",
        answerFormat: "custom",
        options: [
          { value: 1, text: "We give in without much resistance" },
          { value: 2, text: "We struggle and sometimes compromise our boundaries" },
          { value: 3, text: "We try to resist but don't have clear boundaries" },
          { value: 4, text: "We have boundaries and hold each other accountable" },
          { value: 5, text: "We pray together, flee temptation, and honor God with our bodies" }
        ]
      },
      {
        id: 143,
        text: "I understand God's purpose for sex within marriage as a covenant act of love, unity, and fruitfulness.",
        insight: "Evaluates understanding of biblical sexuality and sacred purpose."
      },
      {
        id: 144,
        text: "We have agreed on how to maintain purity and avoid compromising situations (e.g., overnight stays, isolation).",
        insight: "Measures intentionality and boundaries for avoiding temptation."
      },
      {
        id: 145,
        text: "How do you handle conversations about physical affection in your relationship?",
        insight: "Assesses openness and maturity in sensitive communication.",
        answerFormat: "custom",
        options: [
          { value: 1, text: "I avoid these conversations completely" },
          { value: 2, text: "I feel awkward and rarely initiate them" },
          { value: 3, text: "We talk about it occasionally but it feels uncomfortable" },
          { value: 4, text: "We discuss it openly when needed" },
          { value: 5, text: "We communicate freely, honestly, and respectfully about physical affection" }
        ]
      },
      {
        id: 146,
        text: "I believe sexual intimacy should be mutual, respectful, and spiritually conscious within marriage.",
        insight: "Evaluates perspective on love, respect, and mutual satisfaction."
      },
      {
        id: 147,
        text: "How do you view sexual pasts or struggles (e.g., pornography, immorality, abuse)?",
        insight: "Reveals healing status and spiritual wholeness.",
        answerFormat: "custom",
        options: [
          { value: 1, text: "I hide my past and don't want to discuss it" },
          { value: 2, text: "I'm still struggling and haven't sought help" },
          { value: 3, text: "I've shared some things but healing is incomplete" },
          { value: 4, text: "I've been honest and am actively pursuing healing" },
          { value: 5, text: "I've experienced God's restoration and we've discussed everything openly" }
        ]
      },
      {
        id: 148,
        text: "We are accountable to mentors or pastors who guide us in maintaining purity.",
        insight: "Assesses mentorship and submission to spiritual oversight."
      },
      {
        id: 149,
        text: "We have both made a personal commitment to honor God with our bodies before and within marriage.",
        insight: "Tests moral conviction and spiritual commitment to holiness."
      },
      {
        id: 150,
        text: "We understand and agree that sexual intimacy within marriage is not just physical but spiritual and emotional as well.",
        insight: "Evaluates holistic view of marital intimacy."
      }
    ],
    scoringGuidance: {
      excellent: {
        range: [41, 50],
        level: "Excellent",
        message: "Clear spiritual conviction, maturity, and mutual respect regarding sexual boundaries.",
        pastoralGuidance: "Encourage continued accountability and purity mentorship before marriage."
      },
      developing: {
        range: [31, 40],
        level: "Developing",
        message: "Generally disciplined but may need clearer communication and boundaries.",
        pastoralGuidance: "Recommend more structure and shared spiritual practices to stay aligned."
      },
      vulnerable: {
        range: [20, 30],
        level: "Vulnerable",
        message: "Weak boundaries, secrecy, or past wounds affecting purity.",
        pastoralGuidance: "Suggest repentance, counseling, and healing sessions to restore spiritual integrity."
      }
    }
  },

  // ==========================================
  // FACTOR 16: Spiritual Maturity & Shared Faith
  // Questions 151-160
  // ==========================================
  16: {
    name: "Spiritual Maturity & Shared Faith",
    questions: [
      {
        id: 151,
        text: "We regularly pray together and include God in our relationship decisions.",
        insight: "Assesses shared spiritual habits and unity in prayer."
      },
      {
        id: 152,
        text: "We have discussed how we will serve God together as a couple (e.g., ministry, outreach, hospitality).",
        insight: "Measures spiritual purpose alignment and shared calling."
      },
      {
        id: 153,
        text: "How often do you study the Bible or have devotions together?",
        insight: "Evaluates spiritual discipline and growth as a couple.",
        answerFormat: "frequency"
      },
      {
        id: 154,
        text: "We share the same core Christian beliefs and moral values.",
        insight: "Tests doctrinal unity and worldview compatibility."
      },
      {
        id: 155,
        text: "I can discuss my spiritual struggles or doubts openly with my partner without feeling judged.",
        insight: "Evaluates spiritual safety and emotional maturity in faith."
      },
      {
        id: 156,
        text: "How do you handle differences in spiritual practices or convictions (e.g., fasting, worship style)?",
        insight: "Assesses humility and adaptability in faith expressions.",
        answerFormat: "custom",
        options: [
          { value: 1, text: "I insist my way is the right way" },
          { value: 2, text: "It causes tension and we avoid the topic" },
          { value: 3, text: "We tolerate differences but don't discuss them" },
          { value: 4, text: "We respect each other's practices and find common ground" },
          { value: 5, text: "We embrace diversity in expression while staying united in core beliefs" }
        ]
      },
      {
        id: 157,
        text: "We attend church together and view corporate worship as vital to our growth.",
        insight: "Measures engagement with the church community."
      },
      {
        id: 158,
        text: "We both see our marriage as a reflection of Christ's love and service to the church.",
        insight: "Assesses biblical understanding of covenant marriage."
      },
      {
        id: 159,
        text: "When we face challenges, we seek God's guidance together before making major decisions.",
        insight: "Measures spiritual dependence and unity in discernment."
      },
      {
        id: 160,
        text: "We both believe our relationship should glorify God more than it fulfills personal desires.",
        insight: "Tests spiritual priorities and motivation for marriage."
      }
    ],
    scoringGuidance: {
      excellent: {
        range: [41, 50],
        level: "Excellent",
        message: "Spiritually united, faith-driven couple with consistent devotion and shared calling.",
        pastoralGuidance: "Encourage them to mentor others and remain rooted in prayer and Scripture."
      },
      developing: {
        range: [31, 40],
        level: "Developing",
        message: "Growing spiritually together but may lack consistency in devotion or clarity in shared ministry.",
        pastoralGuidance: "Suggest joint devotion schedules, fasting plans, or shared ministry goals."
      },
      vulnerable: {
        range: [20, 30],
        level: "Vulnerable",
        message: "Uneven faith maturity or weak devotional life.",
        pastoralGuidance: "Recommend spiritual mentorship, foundational Bible classes, or premarital faith discipleship sessions."
      }
    }
  },

  // ==========================================
  // FACTOR 17: Decision-Making & Leadership Dynamics
  // Questions 161-170
  // ==========================================
  17: {
    name: "Decision-Making & Leadership Dynamics",
    questions: [
      {
        id: 161,
        text: "When making important decisions, we discuss options and pray together before deciding.",
        insight: "Tests unity and spiritual alignment in decision-making."
      },
      {
        id: 162,
        text: "How are leadership roles handled in your relationship?",
        insight: "Assesses flexibility and balance of power in the relationship.",
        answerFormat: "custom",
        options: [
          { value: 1, text: "One person dominates all decisions" },
          { value: 2, text: "We struggle with power dynamics and tension" },
          { value: 3, text: "We're still figuring out our roles" },
          { value: 4, text: "We share leadership based on our strengths" },
          { value: 5, text: "We lead together biblically — with mutual submission and respect" }
        ]
      },
      {
        id: 163,
        text: "We make decisions by considering both perspectives, even when we disagree.",
        insight: "Evaluates listening skills and fairness during conflict."
      },
      {
        id: 164,
        text: "When we disagree on a major issue, we pause to seek God's direction together.",
        insight: "Tests spiritual humility and dependence on divine guidance."
      },
      {
        id: 165,
        text: "How often do decisions cause lingering tension between you and your partner?",
        insight: "Reveals conflict patterns and maturity in handling disagreements.",
        answerFormat: "frequency_reversed"
      },
      {
        id: 166,
        text: "We clearly understand and respect each other's leadership strengths (e.g., finances, planning, emotions, faith).",
        insight: "Assesses awareness of complementary strengths."
      },
      {
        id: 167,
        text: "In moments of uncertainty, I trust my partner's judgment even when I disagree initially.",
        insight: "Evaluates humility, trust, and willingness to yield."
      },
      {
        id: 168,
        text: "We regularly check in with each other before making decisions that affect us both.",
        insight: "Tests partnership mindset versus individualistic behavior."
      },
      {
        id: 169,
        text: "We understand the biblical concept of leadership and submission as mutual responsibility, not dominance.",
        insight: "Evaluates theological understanding of authority and humility."
      },
      {
        id: 170,
        text: "We can respectfully disagree without feeling like one must always win.",
        insight: "Assesses humility, security, and mutual respect in disagreements."
      }
    ],
    scoringGuidance: {
      excellent: {
        range: [41, 50],
        level: "Excellent",
        message: "Balanced leadership rooted in prayer, humility, and partnership.",
        pastoralGuidance: "Encourage maintaining shared prayer and family vision retreats."
      },
      developing: {
        range: [31, 40],
        level: "Developing",
        message: "Good teamwork, but occasional leadership tension or independence.",
        pastoralGuidance: "Suggest joint decision check-ins and couple devotionals."
      },
      vulnerable: {
        range: [20, 30],
        level: "Vulnerable",
        message: "Poor communication or power imbalance in leadership roles.",
        pastoralGuidance: "Recommend mentoring with a mature couple and biblical study on Ephesians 5."
      }
    }
  },

  // ==========================================
  // FACTOR 18: Forgiveness, Grace & Healing
  // Questions 171-180
  // ==========================================
  18: {
    name: "Forgiveness, Grace & Healing",
    questions: [
      {
        id: 171,
        text: "When my partner offends me, I am quick to forgive and let go of resentment.",
        insight: "Tests readiness to release offense and restore emotional peace."
      },
      {
        id: 172,
        text: "How do you usually handle hurt feelings in your relationship?",
        insight: "Reveals emotional maturity and approach to healing.",
        answerFormat: "custom",
        options: [
          { value: 1, text: "I hold onto hurt and distance myself" },
          { value: 2, text: "I suppress my feelings but feel resentful" },
          { value: 3, text: "I express hurt but struggle to move past it" },
          { value: 4, text: "I communicate my feelings and work toward resolution" },
          { value: 5, text: "I bring my hurt to God, communicate openly, and extend grace" }
        ]
      },
      {
        id: 173,
        text: "We can admit when we are wrong and apologize sincerely to each other.",
        insight: "Assesses humility and responsibility in conflict."
      },
      {
        id: 174,
        text: "I find it easy to move past old issues once we've discussed and resolved them.",
        insight: "Evaluates whether past wounds resurface in new arguments."
      },
      {
        id: 175,
        text: "How do you respond when your partner apologizes?",
        insight: "Tests sincerity and depth of forgiveness.",
        answerFormat: "custom",
        options: [
          { value: 1, text: "I reject apologies or question their sincerity" },
          { value: 2, text: "I accept grudgingly but remind them of the offense" },
          { value: 3, text: "I accept but still feel hurt for a while" },
          { value: 4, text: "I accept graciously and work to restore the relationship" },
          { value: 5, text: "I receive their apology with love, pray together, and fully release the offense" }
        ]
      },
      {
        id: 176,
        text: "We pray together when one of us is hurt or struggling emotionally.",
        insight: "Measures spiritual intimacy and healing through prayer."
      },
      {
        id: 177,
        text: "We have both shared about past hurts or traumas that could affect our relationship.",
        insight: "Assesses emotional openness and vulnerability."
      },
      {
        id: 178,
        text: "When I feel hurt, I seek understanding rather than revenge or silent punishment.",
        insight: "Evaluates maturity and emotional control under offense."
      },
      {
        id: 179,
        text: "We intentionally practice forgiveness as a daily lifestyle, not just after big issues.",
        insight: "Tests continual grace mindset in daily life."
      },
      {
        id: 180,
        text: "We both believe that no offense or mistake is too big for healing through Christ's grace.",
        insight: "Evaluates faith foundation in emotional restoration."
      }
    ],
    scoringGuidance: {
      excellent: {
        range: [41, 50],
        level: "Excellent",
        message: "Deeply compassionate couple; quick to forgive, pray, and restore peace.",
        pastoralGuidance: "Encourage them to model grace to others and maintain prayerful communication."
      },
      developing: {
        range: [31, 40],
        level: "Developing",
        message: "Forgiving but may struggle with emotional residue.",
        pastoralGuidance: "Recommend guided healing conversations and devotionals on mercy."
      },
      vulnerable: {
        range: [20, 30],
        level: "Vulnerable",
        message: "Lingering resentment or pride blocks intimacy.",
        pastoralGuidance: "Recommend forgiveness counseling or pastoral mentoring using Matthew 18:21–35."
      }
    }
  },

  // ==========================================
  // FACTOR 19: Purpose, Calling & Kingdom Partnership
  // Questions 181-190
  // ==========================================
  19: {
    name: "Purpose, Calling & Kingdom Partnership",
    questions: [
      {
        id: 181,
        text: "We see our marriage as a partnership for fulfilling God's purpose, not just for personal happiness.",
        insight: "Tests the spiritual foundation and mission-mindedness of the couple."
      },
      {
        id: 182,
        text: "We have discussed our individual callings and how they complement one another.",
        insight: "Evaluates awareness and respect for each partner's divine assignment."
      },
      {
        id: 183,
        text: "We support each other's ministry, career, and service to God wholeheartedly.",
        insight: "Measures selflessness and unity in serving God through different callings."
      },
      {
        id: 184,
        text: "We regularly pray and seek God's direction for our family's purpose and decisions.",
        insight: "Assesses spiritual leadership and family altar consistency."
      },
      {
        id: 185,
        text: "We have a shared vision for how our home can impact others for God's kingdom.",
        insight: "Evaluates mission-mindedness and hospitality in ministry."
      },
      {
        id: 186,
        text: "How do you view ministry or service in your marriage?",
        insight: "Tests unity in serving and spiritual teamwork.",
        answerFormat: "custom",
        options: [
          { value: 1, text: "Ministry is separate from our marriage relationship" },
          { value: 2, text: "One of us serves while the other is not involved" },
          { value: 3, text: "We serve separately in different areas" },
          { value: 4, text: "We support each other's service and sometimes serve together" },
          { value: 5, text: "Ministry is central to our marriage — we serve God as a united team" }
        ]
      },
      {
        id: 187,
        text: "We are willing to make sacrifices for God's will, even if it disrupts our comfort or plans.",
        insight: "Reveals surrender and obedience to divine assignments."
      },
      {
        id: 188,
        text: "We often discuss how to use our gifts, time, and finances to serve others.",
        insight: "Tests stewardship mindset and generosity orientation."
      },
      {
        id: 189,
        text: "We feel that God brought us together for a specific reason or kingdom purpose.",
        insight: "Assesses faith perception of divine connection."
      },
      {
        id: 190,
        text: "We want our marriage to leave a spiritual legacy that influences generations to come.",
        insight: "Measures long-term purpose orientation and legacy mindset."
      }
    ],
    scoringGuidance: {
      excellent: {
        range: [41, 50],
        level: "Excellent",
        message: "Deeply purpose-driven and spiritually united couple.",
        pastoralGuidance: "Encourage them to document a shared mission statement and serve together intentionally."
      },
      developing: {
        range: [31, 40],
        level: "Developing",
        message: "Spiritually aligned but still defining their kingdom role.",
        pastoralGuidance: "Suggest spiritual retreats or mentorship to clarify calling."
      },
      vulnerable: {
        range: [20, 30],
        level: "Vulnerable",
        message: "Focused mainly on personal happiness over purpose.",
        pastoralGuidance: "Teach about covenant marriage as divine partnership (Gen. 2:18–24, Matt. 19:6)."
      }
    }
  },

  // ==========================================
  // FACTOR 20: Technology & Social Media Boundaries
  // Questions 191-200
  // ==========================================
  20: {
    name: "Technology & Social Media Boundaries",
    questions: [
      {
        id: 191,
        text: "We have discussed what is appropriate and inappropriate to post about our relationship online.",
        insight: "Tests understanding and respect for online privacy boundaries."
      },
      {
        id: 192,
        text: "We are comfortable with each other's level of social media engagement (likes, follows, messages, etc.).",
        insight: "Assesses digital trust and emotional security."
      },
      {
        id: 193,
        text: "We respect each other's need for personal space and privacy regarding devices, without secrecy.",
        insight: "Evaluates the difference between privacy and secrecy."
      },
      {
        id: 194,
        text: "How do you handle phone use during quality time together?",
        insight: "Tests attentiveness and respect during shared time.",
        answerFormat: "custom",
        options: [
          { value: 1, text: "I'm usually on my phone even during conversations" },
          { value: 2, text: "I check my phone frequently but try to limit it" },
          { value: 3, text: "I put my phone aside but occasionally check it" },
          { value: 4, text: "I minimize phone use and give my partner attention" },
          { value: 5, text: "I put my phone away completely and give my full presence" }
        ]
      },
      {
        id: 195,
        text: "We have agreed on sharing passwords or have mutual digital transparency where needed.",
        insight: "Assesses trust level and openness in online spaces."
      },
      {
        id: 196,
        text: "We have healthy boundaries with members of the opposite sex on social media.",
        insight: "Evaluates relational integrity and temptation management."
      },
      {
        id: 197,
        text: "We avoid comparing our relationship to others we see online.",
        insight: "Assesses contentment and self-esteem in the digital age."
      },
      {
        id: 198,
        text: "We both agree that transparency on social media should not replace real communication.",
        insight: "Tests value of authenticity and personal connection."
      },
      {
        id: 199,
        text: "We intentionally create device-free times to focus on each other and family.",
        insight: "Evaluates discipline and intentional connection habits."
      },
      {
        id: 200,
        text: "We believe technology should serve our relationship, not control it.",
        insight: "Measures mindset toward healthy tech use."
      }
    ],
    scoringGuidance: {
      excellent: {
        range: [41, 50],
        level: "Excellent",
        message: "Digitally disciplined couple that values privacy, trust, and presence.",
        pastoralGuidance: "Encourage setting tech boundaries for family time and shared devotional focus."
      },
      developing: {
        range: [31, 40],
        level: "Developing",
        message: "Generally balanced but could define clearer limits and trust norms.",
        pastoralGuidance: "Suggest a 'Digital Covenant' — practical rules for phone use, online respect, and prayer over social media influence."
      },
      vulnerable: {
        range: [20, 30],
        level: "Vulnerable",
        message: "Distracted, secretive, or insecure digital patterns.",
        pastoralGuidance: "Recommend a 7-day 'Digital Fast' and daily in-person communication reflections."
      }
    }
  }
};

// Export factor names for easy reference
export const FACTOR_NAMES = [
  "Emotional Stability & Conflict Resolution",
  "Communication Style & Intimacy",
  "Financial Management & Stewardship",
  "Social Compatibility & Family Dynamics",
  "Life Planning & Flexibility",
  "Love Languages",
  "Personality & Temperament",
  "Spiritual Compatibility & Values",
  "Parenting Philosophy & Family Vision",
  "Sexual Intimacy & Expectations",
  "Extended Family & Boundaries",
  "Career & Life Goals",
  "Emotional Intelligence",
  "Trust & Past Relationships",
  "Sexual & Physical Boundaries",
  "Spiritual Maturity & Shared Faith",
  "Decision-Making & Leadership Dynamics",
  "Forgiveness, Grace & Healing",
  "Purpose, Calling & Kingdom Partnership",
  "Technology & Social Media Boundaries"
];